import { DateTimefromISO, formatDate, formatTime } from '#lib/helper/date.js';
import { CurrencyCode, formatCurrency } from '#lib/helper/currency.js';
import type { TranslationVars } from '#lib/helper/locale.js';

import { AppEvent } from '#lib/types/generated/index.js';
import type { Icon } from '#lib/types/icons.js';
import { DateTime } from 'luxon';

enum NotificationEvent {
	BOOKING_BOOKED = AppEvent.BOOKING_BOOKED,
	BOOKING_CANCELED = AppEvent.BOOKING_CANCELED,
	BOOKING_REVIEW_ANSWERED = AppEvent.BOOKING_REVIEW_ANSWERED,
	GIFTCARD_CONSUMED = AppEvent.GIFTCARD_CONSUMED,
	GIFTCARD_DELETED = AppEvent.GIFTCARD_DELETED,
	GIFTCARD_ORDER_PAYED = AppEvent.GIFTCARD_ORDER_PAYED,
	CUSTOMER_PACKAGE_CREATED = AppEvent.CUSTOMER_PACKAGE_CREATED,
	BOOKING_SPLIT_PAYMENT_DEADLINE_EXPIRED = AppEvent.BOOKING_SPLIT_PAYMENT_DEADLINE_EXPIRED,
	EXPORT_READY = AppEvent.EXPORT_READY
}

type NotificationPayloads = {
	[NotificationEvent.BOOKING_BOOKED]: {
		booking_id: string;
		offer: string;
		pax: number;
		date: string;
		date_human: string;
		time_human: string;
	};
	[NotificationEvent.BOOKING_CANCELED]: {
		booking_id: string;
		offer: string;
		pax: number;
		date: string;
		date_human: string;
		time_human: string;
	};
	[NotificationEvent.BOOKING_REVIEW_ANSWERED]: {
		booking_id: string;
		customer: string;
		date: string;
		date_human: string;
		time_human: string;
	};
	[NotificationEvent.GIFTCARD_CONSUMED]: {
		code: string;
		amount: number;
	};
	[NotificationEvent.GIFTCARD_DELETED]: {
		giftcard_id: string;
	};
	[NotificationEvent.GIFTCARD_ORDER_PAYED]: {
		amount: number;
		quantity: number;
		giftcardOrderId: number;
	};
	[NotificationEvent.CUSTOMER_PACKAGE_CREATED]: {
		amount: number;
		title: string;
	};
	[NotificationEvent.BOOKING_SPLIT_PAYMENT_DEADLINE_EXPIRED]: {
		booking_id: string;
		offer: string;
		pax: number;
		date_human: string;
		remaining_amount: number;
	};
	[NotificationEvent.EXPORT_READY]: {
		exportTitle: string;
		url: string;
		uploadAssetId: number;
		expiresAt?: string | null;
	};
};

type NotificationHandler<T> = {
	transform: (data: T) => TranslationVars;
	getUrl?: (data: T) => string | null;
	icon?: (data: T) => Icon;
};

type NotificationCollection = {
	[K in NotificationEvent]?: NotificationHandler<NotificationPayloads[K]>;
};

export function getNotificationLink(notification: {
	event: string;
	data: Record<string, unknown>;
}): string {
	const handler = notificationCollection[notification.event as NotificationEvent];
	return handler?.getUrl ? (handler.getUrl(notification.data as never) ?? '') : '';
}

export function getParamsTranslation(
	event: string,
	payload: Record<string, unknown>
): TranslationVars {
	const handler = notificationCollection[event as NotificationEvent];
	if (!handler) {
		console.error(
			`Missing event settings in NotificationCollection for ${event} with payload ${JSON.stringify(payload)}`
		);
		return {};
	}
	return handler.transform(payload as never);
}

export function getNotificationIcon(notification: {
	event: string;
	data: Record<string, unknown>;
}): Icon {
	const handler = notificationCollection[notification.event as NotificationEvent];
	return handler?.icon ? handler.icon(notification.data as never) : 'bell';
}

const notificationCollection: NotificationCollection = {
	[NotificationEvent.BOOKING_SPLIT_PAYMENT_DEADLINE_EXPIRED]: {
		transform: (
			data: NotificationPayloads[NotificationEvent.BOOKING_SPLIT_PAYMENT_DEADLINE_EXPIRED]
		): TranslationVars => {
			return {
				offer: data.offer,
				pax: data.pax.toString(),
				date_human: data.date_human,
				remaining_amount: formatCurrency(data.remaining_amount, CurrencyCode.EUR)
			};
		},
		getUrl: (
			data: NotificationPayloads[NotificationEvent.BOOKING_SPLIT_PAYMENT_DEADLINE_EXPIRED]
		) => `/dashboard/calendar/booking/${data.booking_id}`,
		icon: () => 'Split'
	},
	[NotificationEvent.BOOKING_BOOKED]: {
		transform: (
			data: NotificationPayloads[NotificationEvent.BOOKING_BOOKED]
		): TranslationVars => {
			const date = DateTimefromISO(data.date);
			return {
				offer: data.offer,
				pax: data.pax.toString(),
				date_human: date ? formatDate(date).toLowerCase() : data.date_human,
				time_human: date ? formatTime(date) : data.time_human
			};
		},
		getUrl: (data: NotificationPayloads[NotificationEvent.BOOKING_BOOKED]) =>
			`/dashboard/calendar/booking/${data.booking_id}`,
		icon: () => 'calendar'
	},
	[NotificationEvent.BOOKING_CANCELED]: {
		transform: (
			data: NotificationPayloads[NotificationEvent.BOOKING_CANCELED]
		): TranslationVars => {
			const date = DateTimefromISO(data.date);
			return {
				offer: data.offer,
				pax: data.pax.toString(),
				date_human: date ? formatDate(date).toLowerCase() : data.date_human,
				time_human: date ? formatTime(date) : data.time_human
			};
		},
		getUrl: (data: NotificationPayloads[NotificationEvent.BOOKING_CANCELED]) =>
			`/dashboard/calendar/booking/${data.booking_id}`,
		icon: () => 'calendar'
	},
	[NotificationEvent.BOOKING_REVIEW_ANSWERED]: {
		transform: (
			data: NotificationPayloads[NotificationEvent.BOOKING_REVIEW_ANSWERED]
		): TranslationVars => {
			const date = DateTimefromISO(data.date);
			return {
				customer: data.customer,
				date_human: date ? formatDate(date).toLowerCase() : data.date_human,
				time_human: date ? formatTime(date) : data.time_human
			};
		},
		getUrl: (data: NotificationPayloads[NotificationEvent.BOOKING_REVIEW_ANSWERED]) =>
			`/dashboard/reviews?reviewBookingId=${data.booking_id}`,
		icon: () => 'chat'
	},
	[NotificationEvent.GIFTCARD_CONSUMED]: {
		transform: (
			data: NotificationPayloads[NotificationEvent.GIFTCARD_CONSUMED]
		): TranslationVars => {
			return {
				amount: formatCurrency(data.amount, CurrencyCode.EUR),
				code: data.code
			};
		},
		getUrl: () => `/dashboard/giftcards`,
		icon: () => 'giftcard'
	},
	[NotificationEvent.GIFTCARD_DELETED]: {
		transform: (
			data: NotificationPayloads[NotificationEvent.GIFTCARD_DELETED]
		): TranslationVars => {
			return {
				giftcard_id: data.giftcard_id
			};
		}
	},
	[NotificationEvent.GIFTCARD_ORDER_PAYED]: {
		transform: (
			data: NotificationPayloads[NotificationEvent.GIFTCARD_ORDER_PAYED]
		): TranslationVars => {
			return {
				amount: formatCurrency(data.amount, CurrencyCode.EUR),
				quantity: data.quantity.toString()
			};
		},
		icon: () => 'giftcard'
	},
	[NotificationEvent.CUSTOMER_PACKAGE_CREATED]: {
		transform: (
			data: NotificationPayloads[NotificationEvent.CUSTOMER_PACKAGE_CREATED]
		): TranslationVars => {
			return {
				amount: formatCurrency(data.amount, CurrencyCode.EUR),
				title: data.title
			};
		},
		icon: () => 'Blocks'
	},
	[NotificationEvent.EXPORT_READY]: {
		transform: (
			data: NotificationPayloads[NotificationEvent.EXPORT_READY]
		): TranslationVars => {
			return {
				name: data.exportTitle
			};
		},
		getUrl: (data: NotificationPayloads[NotificationEvent.EXPORT_READY]) => {
			if (data.expiresAt && DateTime.fromISO(data.expiresAt) <= DateTime.now()) {
				return null;
			}
			return data.url;
		},
		icon: () => 'export'
	}
} as const;
